/**
 * CSS Variable Injection & Document Class Management
 *
 * Handles applying theme tokens (colors, typography, spacing, animations)
 * as CSS custom properties and managing document-level class names.
 *
 */

import type { Theme, ThemePreferences } from './types';

// CSS VARIABLE INJECTION

/**
 * Scale a font-size string by the given factor.
 */
function scaledFontSize(size: string, fontScale: number): string {
  const value = parseFloat(size);
  return `${value * fontScale}px`;
}

/**
 * Inject all CSS custom properties derived from a theme onto `document.documentElement`.
 */
export function injectCSSVariables(theme: Theme, settings: ThemePreferences['settings']): void {
  const root = document.documentElement;
  const { colors, typography, spacing, animations } = theme;

  // Color variables
  Object.entries(colors).forEach(([key, value]) => {
    const cssVarName = `--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    root.style.setProperty(cssVarName, value);
  });

  // Typography variables
  root.style.setProperty('--font-family', typography.fontFamily);
  root.style.setProperty('--font-family-mono', typography.fontFamilyMono);
  root.style.setProperty(
    '--font-size-base',
    scaledFontSize(typography.fontSizeBase, settings.fontScale)
  );
  root.style.setProperty(
    '--font-size-sm',
    scaledFontSize(typography.fontSizeSmall, settings.fontScale)
  );
  root.style.setProperty(
    '--font-size-lg',
    scaledFontSize(typography.fontSizeLarge, settings.fontScale)
  );
  root.style.setProperty(
    '--font-size-xl',
    scaledFontSize(typography.fontSizeXL, settings.fontScale)
  );
  root.style.setProperty(
    '--font-size-xxl',
    scaledFontSize(typography.fontSizeXXL, settings.fontScale)
  );
  root.style.setProperty('--line-height-normal', typography.lineHeightNormal);
  root.style.setProperty('--line-height-tight', typography.lineHeightTight);
  root.style.setProperty('--line-height-loose', typography.lineHeightLoose);

  // Spacing variables
  root.style.setProperty('--spacing-unit', `${spacing.unit}px`);
  root.style.setProperty('--spacing-xs', spacing.xs);
  root.style.setProperty('--spacing-sm', spacing.sm);
  root.style.setProperty('--spacing-md', spacing.md);
  root.style.setProperty('--spacing-lg', spacing.lg);
  root.style.setProperty('--spacing-xl', spacing.xl);
  root.style.setProperty('--spacing-xxl', spacing.xxl);
  root.style.setProperty('--border-radius', spacing.borderRadius);
  root.style.setProperty('--border-radius-lg', spacing.borderRadiusLarge);
  root.style.setProperty('--border-radius-full', spacing.borderRadiusFull);

  // Animation variables
  root.style.setProperty('--duration-fast', animations.durationFast);
  root.style.setProperty('--duration-normal', animations.durationNormal);
  root.style.setProperty('--duration-slow', animations.durationSlow);
  root.style.setProperty('--easing-default', animations.easingDefault);
  root.style.setProperty('--easing-emphasized', animations.easingEmphasized);

  // Message spacing
  root.style.setProperty('--message-spacing', `${16 * settings.messageSpacing}px`);

  // Color scheme (native browser integration)
  root.style.setProperty(
    'color-scheme',
    theme.colorScheme ?? (theme.category === 'light' ? 'light' : 'dark')
  );

  // Accent color for native form controls — matches each theme's brand
  const accentColorMap: Record<string, string> = {
    aurora: '#8b5cf6',
    dark: '#DFFF0A',
    light: '#2563eb',
    bubble: '#8B5CF6',
  };
  root.style.setProperty('accent-color', accentColorMap[theme.id] ?? theme.colors.primary);

  // Glass config CSS variables
  if (theme.glassConfig) {
    root.style.setProperty('--glass-card-bg', theme.glassConfig.cardBackground);
    root.style.setProperty('--glass-card-border', theme.glassConfig.cardBorder);
    root.style.setProperty('--glass-card-blur', theme.glassConfig.cardBlur);
  }

  // Button style CSS variables
  if (theme.buttonStyle) {
    root.style.setProperty('--btn-family', theme.buttonStyle.family);
    root.style.setProperty('--btn-primary-bg', theme.buttonStyle.primaryBg);
    root.style.setProperty('--btn-primary-hover', theme.buttonStyle.primaryHover);
    root.style.setProperty('--btn-radius', theme.buttonStyle.borderRadius);
    root.style.setProperty('--btn-shadow', theme.buttonStyle.shadow);
    root.style.setProperty('--btn-hover-shadow', theme.buttonStyle.hoverShadow);
    root.style.setProperty('--btn-active-shadow', theme.buttonStyle.activeShadow);
  }

  // Set <meta name="theme-color"> to background primary
  if (typeof document.querySelector === 'function') {
    let metaThemeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta');
      metaThemeColor.name = 'theme-color';
      document.head?.appendChild(metaThemeColor);
    }
    metaThemeColor.content = colors.background;
  }
}

// DOCUMENT CLASS MANAGEMENT

/**
 * Update document-level CSS classes to reflect the active theme
 * and current accessibility / display settings.
 */
export function updateDocumentClasses(theme: Theme, settings: ThemePreferences['settings']): void {
  const root = document.documentElement;

  // Remove existing theme classes
  root.classList.remove(
    'light',
    'dark',
    'theme-aurora',
    'theme-dark',
    'theme-light',
    'theme-bubble'
  );

  // Add category class (light/dark)
  if (theme.category === 'light') {
    root.classList.add('light');
  } else {
    root.classList.add('dark');
  }

  // Add variant class for theme-specific styling
  if (theme.variant) {
    root.classList.add(`theme-${theme.variant}`);
  }

  // Accessibility classes
  if (settings.reduceMotion) {
    root.classList.add('reduce-motion');
  } else {
    root.classList.remove('reduce-motion');
  }

  if (settings.highContrast) {
    root.classList.add('high-contrast');
  } else {
    root.classList.remove('high-contrast');
  }

  // Message display
  root.classList.remove('message-cozy', 'message-compact');
  root.classList.add(`message-${settings.messageDisplay}`);
}
